import React, { useMemo } from "react";
import { useCacheInvalidation } from "@hooks/useCacheInvalidation";
import { AppShell } from "@design-system/AppShell/AppShell";
import { SideNavProvider } from "@design-system/SideNav/SideNavProvider";
import { WorkspaceSideNav } from "../navigation/sideNav/WorkspaceSideNav";
import { ChildrenProps } from "../../types";
import { Breadcrumb, MainInterfaceHeader } from "./MainInterfaceHeader";
import { MainViewUserDropdownItems } from "./UserDropdown/MainViewUserDropdownItems";
import { OrganizationHeader } from "./OrganizationHeader/OrganizationHeader";
import { SubNavigationPortalProvider } from "./SubNavigationPortalProvider";
import { ChatContainer } from "./chat/ChatContainer";
import { AgentChatWidget } from "./agentChat/AgentChatWidget";

const DEFAULT_DROPDOWN_ITEMS = <MainViewUserDropdownItems />;
const SIDE_NAV_STATE_STORAGE_KEY = "kick:side-nav:state";
const LEGACY_SIDE_NAV_HIDDEN_STORAGE_KEY = "kick:side-nav:hidden";

export interface MainInterfaceProps extends ChildrenProps {
    /** The `SideNav` for the page; the workspace nav when omitted, no nav when `null`. */
    sideNavigation?: React.ReactElement | null;
    subNavigation?: React.ReactNode;
    dropdownItems?: React.ReactNode;
    breadcrumbs?: Breadcrumb[];
    fullPage?: boolean;
    containerClassName?: string;
    showSubNavigation?: boolean;
    contained?: boolean;
}

export const MainInterface: React.FC<MainInterfaceProps> = ({
    children,
    sideNavigation,
    dropdownItems,
    subNavigation,
    breadcrumbs,
    containerClassName,
    fullPage,
    showSubNavigation = false,
    contained = false,
}) => {
    useCacheInvalidation();
    const sideNav =
        sideNavigation === undefined ? <WorkspaceSideNav /> : sideNavigation;

    // Stabilise the dropdownItems reference so React.memo on MainInterfaceHeader
    // can skip re-renders when nothing in the header has actually changed.
    const resolvedDropdownItems = useMemo(
        () => dropdownItems ?? DEFAULT_DROPDOWN_ITEMS,
        [dropdownItems],
    );

    const shell = (
        <>
            <AppShell
                navigation={sideNav}
                header={
                    <MainInterfaceHeader
                        breadcrumbs={breadcrumbs}
                        dropdownItems={resolvedDropdownItems}
                    />
                }
                banner={<OrganizationHeader />}
                subNavigation={subNavigation}
                showSubNavigation={showSubNavigation}
                aside={<ChatContainer />}
                contained={contained}
                fullPage={fullPage}
                containerClassName={containerClassName}
            >
                <SubNavigationPortalProvider>
                    {children}
                </SubNavigationPortalProvider>
            </AppShell>
            <AgentChatWidget />
        </>
    );

    return sideNav === null ? (
        shell
    ) : (
        <SideNavProvider
            storageKey={SIDE_NAV_STATE_STORAGE_KEY}
            legacyHiddenStorageKey={LEGACY_SIDE_NAV_HIDDEN_STORAGE_KEY}
        >
            {shell}
        </SideNavProvider>
    );
};
